import { cn } from '../../services/utils';

export type ContentMaxWidth =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'full'
  | 'none';

const maxWidthClasses: Record<ContentMaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
  none: '',
};

interface AppContentContainerProps {
  children: React.ReactNode;
  /** Maximum width of the content container. Default: '7xl' (1280px) */
  maxWidth?: ContentMaxWidth;
  /** Additional CSS classes */
  className?: string;
  /** Add standard padding (px-4 py-6). Default: true */
  padding?: boolean;
}

/**
 * Shared content container component with configurable max width.
 * Use this to wrap page content with consistent padding and width constraints.
 *
 * @example
 * // Standard width (1280px max)
 * <AppContentContainer>...</AppContentContainer>
 *
 * // Full width
 * <AppContentContainer maxWidth="full">...</AppContentContainer>
 *
 * // Custom width without padding
 * <AppContentContainer maxWidth="4xl" padding={false}>...</AppContentContainer>
 */
export function AppContentContainer({
  children,
  maxWidth = '7xl',
  className,
  padding = true,
}: AppContentContainerProps) {
  return (
    <div
      className={cn(
        'mx-auto',
        padding && 'px-4 py-6',
        maxWidth !== 'none' && maxWidthClasses[maxWidth],
        className
      )}
    >
      {children}
    </div>
  );
}
